import React from 'react'
import { Link } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import { useOutsideClick } from 'rooks'
import { SvgLazy } from '@mobi/component-library/Common/Svg/SvgLazy'
import { TabButton, Tabs } from '@mobi/component-library/Navigation/Tabs'
import { Tooltip } from '@mobi/component-library/Common/V2/Tooltip'
import { PageHeader } from '@mobi/component-library/Navigation/PageHeader'
import { AppRoutes } from '@core/AppRoutes'
import { MainContainer } from '@core/Components/Containers'
import { useSearchParamState } from '@core/Utils/hooks/useSearchParamState'
import type { FollowMeTabNav } from '@core/Areas/FollowMe/types'
import { FollowingFeed } from '@core/Areas/FollowMe/Components/FollowingFeed'
import { ExploreFeed } from '@core/Areas/FollowMe/Components/ExploreFeed'
import { ProfileAvatar } from '@core/Areas/FollowMe/Components/Common/ProfileAvatar'
import { trackBetLoopViewMyProfile } from '@core/Areas/FollowMe/helpers/analytics'
import { followMePrincipleProfileQuery } from '@core/Areas/FollowMe/helpers/queries'
import { useOnboardingTooltips } from '@core/Areas/FollowMe/hooks/useOnboardingTooltips'

export const FeedPage = () => {
  const { data: myProfile, isPending } = useQuery(followMePrincipleProfileQuery)

  const [currentTab, setCurrentTab] = useSearchParamState<FollowMeTabNav>({
    key: 'tab',
    values: ['explore', 'following'],
    defaultValue: 'explore',
  })

  const viewProfileTooltipRef = React.useRef<HTMLDivElement>(null)

  const { currentTooltipId, onDismissed } = useOnboardingTooltips('bet-loop-view-my-profile')
  const shouldShowTooltip = currentTooltipId === 'bet-loop-view-my-profile'
  useOutsideClick(viewProfileTooltipRef, () => {
    if (shouldShowTooltip) onDismissed('bet-loop-view-my-profile')
  })

  return (
    <MainContainer background='surface300'>
      <PageHeader
        title={<SvgLazy name='BetLoopHeaderLogo' height='4rem' color='white' />}
        rightAlignedComponent={
          myProfile ? (
            <Link to={AppRoutes.BetLoopMyProfile} onClick={trackBetLoopViewMyProfile}>
              <div ref={viewProfileTooltipRef}>
                <ProfileAvatar avatar={myProfile?.avatar} color={myProfile.color} />
              </div>
            </Link>
          ) : null
        }
      />

      {shouldShowTooltip && (
        <Tooltip
          color='blue'
          hasTooltipBeenSeen={() => false}
          isHidden={isPending}
          shouldHideCrossIcon
          triggerElRef={viewProfileTooltipRef}
          tooltipId='bet-loop-view-my-profile'
          offsetYPx={7}
          offsetXPx={-55}
          flipElement={true}
          shouldOnlyRenderSingleInstance={true}
          onDismissed={() => onDismissed('bet-loop-view-my-profile')}
        >
          <div>View and edit your</div>
          <div>own profile here</div>
        </Tooltip>
      )}

      <Tabs>
        <TabButton
          content='Explore'
          isSelected={currentTab === 'explore'}
          onClick={() => setCurrentTab('explore')}
        />

        <TabButton
          content='Following'
          isSelected={currentTab === 'following'}
          onClick={() => setCurrentTab('following')}
        />
      </Tabs>

      <div>
        {currentTab === 'explore' ? <ExploreFeed /> : null}

        {currentTab === 'following' ? <FollowingFeed /> : null}
      </div>
    </MainContainer>
  )
}
